import { useState, useEffect } from 'react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Plus, Edit2, Power, Trash2, CheckCircle, XCircle } from 'lucide-react';
import axios from 'axios';
import './AdminStyles.css';

// Используем относительный путь для админ панели
const API_URL = '/api';

function AdminPlansPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    plan_code: '',
    name: '',
    description: '',
    documents_limit: '',
    price: '',
    currency: 'UZS',
    duration_days: 30,
    is_active: true,
    sort_order: 0,
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/admin/plans`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlans(response.data.plans);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch plans:', err);
      setError('Не удалось загрузить тарифы');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingPlan(null);
    setFormData({
      plan_code: '',
      name: '',
      description: '',
      documents_limit: '',
      price: '',
      currency: 'UZS',
      duration_days: 30,
      is_active: true,
      sort_order: 0,
    });
    setShowModal(true);
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      plan_code: plan.plan_code,
      name: plan.name,
      description: plan.description || '',
      documents_limit: plan.documents_limit,
      price: plan.price,
      currency: plan.currency,
      duration_days: plan.duration_days,
      is_active: plan.is_active,
      sort_order: plan.sort_order,
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('admin_token');
      
      if (editingPlan) {
        // Update
        await axios.put(
          `${API_URL}/admin/plans/${editingPlan.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        // Create
        await axios.post(
          `${API_URL}/admin/plans`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }

      setShowModal(false);
      fetchPlans();
    } catch (err) {
      console.error('Save plan error:', err);
      alert(err.response?.data?.error || 'Ошибка сохранения');
    }
  };

  const handleToggleStatus = async (planId) => {
    try {
      const token = localStorage.getItem('admin_token');
      await axios.patch(
        `${API_URL}/admin/plans/${planId}/toggle`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchPlans();
    } catch (err) {
      console.error('Toggle plan error:', err);
      alert('Ошибка изменения статуса');
    }
  };

  const handleDelete = async (planId) => {
    if (!confirm('Вы уверены, что хотите удалить этот тариф?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      await axios.delete(`${API_URL}/admin/plans/${planId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchPlans();
    } catch (err) {
      console.error('Delete plan error:', err);
      alert('Ошибка удаления');
    }
  };

  if (loading) {
    return (
      <AdminLayout currentPage="plans">
        <div className="admin-page" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
          <div className="admin-loader"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout currentPage="plans">
        <div className="admin-page" style={{ textAlign: 'center', padding: '48px 24px' }}>
          <p style={{ color: '#ef4444', fontSize: '18px', marginBottom: '16px' }}>{error}</p>
          <button onClick={fetchPlans} className="admin-button admin-button-primary">
            Повторить
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout currentPage="plans">
      <div className="admin-page">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
          <div>
            <h1 style={{ fontSize: '32px', fontWeight: '700', color: '#111827', marginBottom: '8px' }}>
              Тарифные планы
            </h1>
            <p style={{ color: '#6b7280' }}>Управление тарифами подписок</p>
          </div>
          <button onClick={handleCreate} className="admin-button admin-button-primary">
            <Plus size={20} />
            <span>Добавить тариф</span>
          </button>
        </div>

        <div className="admin-table">
          <table>
            <thead>
              <tr>
                <th>Код</th>
                <th>Название</th>
                <th>Документов</th>
                <th>Цена</th>
                <th>Длительность</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {plans.map((plan) => (
                <tr key={plan.id}>
                  <td className="font-mono text-xs" style={{ color: '#6b7280' }}>
                    {plan.plan_code}
                  </td>
                  <td>
                    <div style={{ fontWeight: '600', color: '#111827' }}>{plan.name}</div>
                    {plan.description && (
                      <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                        {plan.description}
                      </div>
                    )}
                  </td>
                  <td style={{ fontWeight: '600', color: '#111827' }}>
                    {plan.documents_limit}
                  </td>
                  <td style={{ fontWeight: '700', color: '#111827' }}>
                    {parseFloat(plan.price).toLocaleString()} {plan.currency}
                  </td>
                  <td style={{ color: '#6b7280' }}>{plan.duration_days} дней</td>
                  <td>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        onClick={() => handleEdit(plan)}
                        className="admin-button admin-button-secondary"
                        style={{ padding: '6px 12px' }}
                        title="Редактировать"
                      >
                        <Edit2 size={16} />
                      </button>
                      <button
                        onClick={() => handleToggleStatus(plan.id)}
                        className={`admin-button ${plan.is_active ? 'admin-button-secondary' : 'admin-button-success'}`}
                        style={{ padding: '6px 12px' }}
                        title={plan.is_active ? 'Деактивировать' : 'Активировать'}
                      >
                        <Power size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(plan.id)}
                        className="admin-button admin-button-danger"
                        style={{ padding: '6px 12px' }}
                        title="Удалить"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {plans.length === 0 && (
            <div className="empty-state">
              <p className="empty-state-title">Нет тарифов</p>
              <p className="empty-state-description">Создайте первый тарифный план</p>
            </div>
          )}
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '90vh',
            overflow: 'auto',
          }}>
            <h2 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '24px' }}>
              {editingPlan ? 'Редактировать тариф' : 'Создать тариф'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                    Код тарифа *
                  </label>
                  <input
                    type="text"
                    value={formData.plan_code}
                    onChange={(e) => setFormData({ ...formData, plan_code: e.target.value })}
                    required
                    disabled={!!editingPlan}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '14px',
                    }}
                    placeholder="basic_1000"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                    Название *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '14px',
                    }}
                    placeholder="Базовый 1000"
                  />
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                    Описание
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    style={{
                      width: '100%',
                      padding: '10px',
                      border: '1px solid #e5e7eb',
                      borderRadius: '6px',
                      fontSize: '14px',
                    }}
                    placeholder="Подписка на 1000 документов в месяц"
                  />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                      Лимит документов *
                    </label>
                    <input
                      type="number"
                      value={formData.documents_limit}
                      onChange={(e) => setFormData({ ...formData, documents_limit: parseInt(e.target.value) })}
                      required
                      min="1"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '14px',
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                      Длительность (дней) *
                    </label>
                    <input
                      type="number"
                      value={formData.duration_days}
                      onChange={(e) => setFormData({ ...formData, duration_days: parseInt(e.target.value) })}
                      required
                      min="1"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '14px',
                      }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                      Цена *
                    </label>
                    <input
                      type="number"
                      value={formData.price}
                      onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) })}
                      required
                      min="0"
                      step="0.01"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '14px',
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '600', fontSize: '14px' }}>
                      Валюта
                    </label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '14px',
                      }}
                    >
                      <option value="UZS">UZS</option>
                      <option value="USD">USD</option>
                      <option value="RUB">RUB</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <span style={{ fontWeight: '600', fontSize: '14px' }}>Активен для продажи</span>
                  </label>
                </div>

                <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                  <button type="submit" className="admin-button admin-button-primary" style={{ flex: 1, padding: '14px 24px', fontSize: '15px' }}>
                    {editingPlan ? 'Сохранить' : 'Создать'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="admin-button admin-button-secondary"
                    style={{ flex: 1, padding: '14px 24px', fontSize: '15px' }}
                  >
                    Отмена
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default AdminPlansPage;

